import api from './client'

export type LoanEntryType = 'EMI' | 'PREPAYMENT' | 'INCREASE'

export interface LoanEntry {
  id: string
  type: LoanEntryType
  date: string
  /** Principal repaid (EMI / PREPAYMENT) or the amount added to the outstanding (INCREASE). */
  principalAmount: number
  interestAmount: number
  /** Paid from (EMI / PREPAYMENT) or received into (INCREASE); null when no account moved. */
  accountId: string | null
}

export interface LoanDetail {
  id: string
  name: string
  lender: string | null
  /** Pre-fill for the Pay EMI form only. */
  emiAmount: number | null
  /** Pre-fill for the Pay EMI form only. */
  emiAccountId: string | null
  archivedAt: string | null
  outstanding: number
  principalRepaid: number
  interestPaid: number
  entries: LoanEntry[]
}

export interface CreateLoanPayload {
  type: 'LOAN'
  name: string
  lender?: string
  /** The principal still owed today. */
  outstanding: number
  emiAmount?: number
  emiAccountId?: string
  /** The account the loan money was received into; omit when no account moved. */
  receivedIntoAccountId?: string
  /** Date the money was received; only used with receivedIntoAccountId. */
  startDate?: string
}

export interface RecordLoanPaymentPayload {
  type: 'EMI' | 'PREPAYMENT'
  accountId: string
  date: string
  principalAmount: number
  interestAmount?: number
}

export interface RecordLoanIncreasePayload {
  amount: number
  date: string
  accountId?: string
}

export interface UpdateLoanPayload {
  lender: string | null
  emiAmount: number | null
  emiAccountId: string | null
}

export async function getLoanDetail(loanId: string): Promise<LoanDetail> {
  const { data } = await api.get<LoanDetail>(`/loans/${loanId}`)
  return data
}

export async function updateLoan(loanId: string, payload: UpdateLoanPayload): Promise<LoanDetail> {
  const { data } = await api.patch<LoanDetail>(`/loans/${loanId}`, payload)
  return data
}

export async function recordLoanPayment(
  loanId: string,
  payload: RecordLoanPaymentPayload
): Promise<void> {
  await api.post(`/loans/${loanId}/payments`, payload)
}

export async function recordLoanIncrease(
  loanId: string,
  payload: RecordLoanIncreasePayload
): Promise<void> {
  await api.post(`/loans/${loanId}/increases`, payload)
}

export async function deleteLoanEntry(loanId: string, entryId: string): Promise<void> {
  await api.delete(`/loans/${loanId}/entries/${entryId}`)
}
